import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { toast } from 'react-toastify';
import ResendModal from './ResendModal';
import RefundModal from '../../ConfirmDashboard/components/RefundModal';
import PrintModal from './PrintModal';
import TransferModal from './TransferModal';
import useOrderRefund from '../hooks/useOrderRefund';
import useOrderPrint from '../hooks/useOrderPrint';
import useOrderTransfer from '../hooks/useOrderTransfer';

const OrderActions = ({ order, buyer, urls, capabilities, printing, csrfToken, team, currentUser, confirm, refundReasons, venueOwedBalance, canBypassRefundRestrictions, tickets, recipientCount, refundPrefill }) => {
  const [resendOpen, setResendOpen] = useState(false);
  const { refund, submittingRefund, refundChanged, fetchNewRefund, createRefund, confirmRefund } = useOrderRefund({ csrfToken });
  const prefillOpened = useRef(false);

  useEffect(() => {
    if (!refundPrefill) return;
    fetchNewRefund(csrfToken, team, order, refundPrefill.ticket, refundPrefill.amount, () => {
      toast.error('The ticket changed, but the difference could not be refunded here. Use Refund to refund it.', { ...TOAST_OPTS, autoClose: 5000 });
      setTimeout(() => window.location.reload(), 5000);
    });
  }, [refundPrefill]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!refundPrefill) return;
    if (Object.keys(refund).length > 0) prefillOpened.current = true;
    else if (prefillOpened.current) window.location.reload();
  }, [refund, refundPrefill]);
  const { open: printOpen, openModal: openPrint, closeModal: closePrint, printers, loading: loadingPrinters, selected: selectedPrinter, setSelected: setSelectedPrinter, confirmPrint } = useOrderPrint({ csrfToken, printing });
  const transferableTickets = (tickets || []).filter((t) => t.transferable);
  const { open: transferOpen, openModal: openTransfer, closeModal: closeTransfer, submitting: transferSubmitting, submit: submitTransfer } = useOrderTransfer({ csrfToken, transferUrl: urls.ticketTransfers, onTransferred: () => window.location.reload() });

  const TOAST_OPTS = { position: toast.POSITION.TOP_CENTER, hideProgressBar: true };

  const post = (url, successMsg) => {
    axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
    axios.post(url, {})
      .then(({ data }) => {
        // A resold seat is refused even when the rest of the order checks in. "Checked in"
        // alone would tell the operator a fan got in who did not.
        const refusals = (data && data.refusals) || [];
        if (refusals.length === 0) return toast.success(successMsg, TOAST_OPTS);

        const reasons = [...new Set(refusals.map((r) => r.failure_reason))].join('. ');
        return toast.warn(`${refusals.length} ticket(s) not admitted. ${reasons}`, TOAST_OPTS);
      })
      .catch(() => toast.error('Something went wrong', TOAST_OPTS));
  };

  return (
    <div className="op-actions">
      {capabilities.ticketActions && (
        <button className="op-btn" onClick={() => setResendOpen(true)}>Resend</button>
      )}
      {urls.pdfUrl && (
        <a className="op-btn" href={urls.pdfUrl} target="_blank" rel="noopener noreferrer">Download PDF</a>
      )}
      {capabilities.canPrint && (
        <button className="op-btn" onClick={openPrint}>Print</button>
      )}
      {capabilities.ticketActions && (
        <>
          <button className="op-btn" onClick={() => post(urls.checkIn, 'Checked in')}>Check in</button>
          <button className="op-btn" onClick={() => post(urls.checkOut, 'Checked out')}>Check out</button>
        </>
      )}
      {capabilities.ticketActions && transferableTickets.length > 0 && (
        <button className="op-btn" onClick={openTransfer}>Transfer</button>
      )}
      {capabilities.canRefund && capabilities.canManageRefunds && (
        <button className="op-btn op-btn-danger" onClick={() => fetchNewRefund(csrfToken, team, order)}>Refund</button>
      )}

      <ResendModal
        open={resendOpen}
        onClose={() => setResendOpen(false)}
        sendReceiptUrl={urls.sendReceipt}
        recipientsUrl={urls.sendReceiptToRecipients}
        recipientCount={recipientCount}
        defaultEmail={buyer.email}
        csrfToken={csrfToken}
      />
      <PrintModal
        open={printOpen}
        onClose={closePrint}
        onConfirm={confirmPrint}
        printing={printing}
        printers={printers}
        loading={loadingPrinters}
        selected={selectedPrinter}
        setSelected={setSelectedPrinter}
        isAdmin={currentUser && currentUser.admin}
      />
      <RefundModal
        csrfToken={csrfToken}
        team={team}
        currentUser={currentUser}
        confirm={confirm}
        refund={refund}
        fetchNewRefund={fetchNewRefund}
        refundChanged={refundChanged}
        refundReasons={refundReasons}
        createRefund={createRefund}
        submittingRefund={submittingRefund}
        confirmRefund={confirmRefund}
        venueOwedBalance={venueOwedBalance}
        canBypassRefundRestrictions={canBypassRefundRestrictions}
      />
      <TransferModal
        open={transferOpen}
        onClose={closeTransfer}
        onSubmit={submitTransfer}
        tickets={transferableTickets}
        submitting={transferSubmitting}
      />
    </div>
  );
};

OrderActions.propTypes = {
  order: PropTypes.object.isRequired,
  buyer: PropTypes.object,
  urls: PropTypes.object.isRequired,
  capabilities: PropTypes.object.isRequired,
  printing: PropTypes.object,
  csrfToken: PropTypes.string,
  team: PropTypes.object.isRequired,
  currentUser: PropTypes.object,
  confirm: PropTypes.object,
  refundReasons: PropTypes.arrayOf(PropTypes.string),
  venueOwedBalance: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  canBypassRefundRestrictions: PropTypes.bool,
  tickets: PropTypes.array,
  recipientCount: PropTypes.number,
  refundPrefill: PropTypes.shape({
    ticket: PropTypes.shape({ id: PropTypes.number.isRequired, barcode: PropTypes.string }).isRequired,
    amount: PropTypes.number.isRequired,
  }),
};

OrderActions.defaultProps = {
  buyer: {},
  printing: null,
  csrfToken: null,
  currentUser: null,
  confirm: null,
  refundReasons: [],
  venueOwedBalance: null,
  canBypassRefundRestrictions: false,
  tickets: [],
  recipientCount: 0,
  refundPrefill: null,
};

export default OrderActions;
